import { useEffect, useState } from "react";
import { Button, Form, Modal, Table, Badge, Card, Row, Col } from "react-bootstrap";
import Pagination from 'react-js-pagination';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faHome, faCheckCircle, faTimesCircle, faUser, faCalendarAlt, faTrash, faBuilding, faMapMarkerAlt, faInfoCircle, faPhone } from '@fortawesome/free-solid-svg-icons';

import ViewItemsPerPage from "../ViewItemsPerPage";
import { Link } from "react-router-dom";
import { Enumeration, OptionType, ListProperty, SearchProperty, StatusItem } from "../../utils/interface";
import SearchableDropdown from "../SearchableDropdown";
import { formatEnumsForDropDown, formatStatuesForDropDown } from "../../utils/helperFunction";

interface ListPropertiesProps {
  propertiesList: ListProperty[];
  propertiesListCount: number;
  propertiesListPageIndex: number;
  propertiesListPageSize: number;
  onChangePageIndex: (pageNumber: number) => void;
  onChangePageSize: (pageSize: number) => void;
  findOptions: SearchProperty;
  handleFindProperty: (findOptions: SearchProperty) => void;
  resetFindOptions: () => void;
  propertyStatuses: StatusItem[];
  propertyTypes: Enumeration[];
  fetchOwners: (searchTerm: string, pageIndex: number, pageSize: number) =>
    Promise<{ options: { value: number; label: string; }[], hasMore: boolean }>;
  onDeleteProperty: (propertyId: number) => void;
}

const ListProperties: React.FC<ListPropertiesProps> = ({
  propertiesList,
  propertiesListCount,
  propertiesListPageIndex,
  propertiesListPageSize,
  onChangePageIndex,
  onChangePageSize,
  findOptions,
  handleFindProperty,
  resetFindOptions,
  fetchOwners,
  propertyStatuses,
  propertyTypes,
  onDeleteProperty,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [searchOptions, setSearchOptions] = useState(findOptions);
  const [selectedOwner, setSelectedOwner] = useState<OptionType | null>(null);

  const handleModalClose = () => setShowModal(false);
  const handleModalOpen = () => setShowModal(true);

  const onChangeFieldValue = (value: object) => {
    setSearchOptions({ ...searchOptions, ...value });
  }
  const onClickFind = (findOptions: SearchProperty) => {
    handleFindProperty(findOptions);
    handleModalClose();
  };
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeFieldValue({ isVerified: event.target.value === 'yes' });
  };

  useEffect(() => { setSearchOptions(findOptions) }, [findOptions])

  return (
    <div className="property-list-container">
      <style>{`
        .property-list-container {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .modern-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          background: #fff;
          overflow: hidden;
        }
        .modern-table {
          margin-bottom: 0;
        }
        .modern-table thead th {
          background-color: #f8f9fa;
          color: #495057;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
          padding: 1rem;
        }
        .modern-table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f5;
        }
        .property-icon {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          margin-right: 15px;
          box-shadow: 0 4px 10px rgba(49, 130, 206, 0.3);
          flex-shrink: 0;
        }
        .icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .icon-btn:hover {
          transform: translateY(-2px);
        }
        @media (max-width: 768px) {
          .modern-table thead {
            display: none;
          }
          .modern-table tbody, .modern-table tr, .modern-table td {
            display: block;
            width: 100%;
          }
          .modern-table tr {
            margin-bottom: 1rem;
            border: 1px solid #e9ecef;
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            background: #fff;
          }
          .modern-table td {
            text-align: right;
            padding-left: 40%;
            position: relative;
            border-bottom: 1px solid #f8f9fa;
          }
          .modern-table td::before {
            content: attr(data-label);
            position: absolute;
            left: 1rem;
            width: 35%;
            padding-right: 10px;
            white-space: nowrap;
            text-align: left;
            font-weight: 600;
            color: #6c757d;
            font-size: 0.85rem;
          }
          .modern-table td:last-child {
            border-bottom: 0;
            display: flex;
            justify-content: flex-end;
            gap: 10px;
          }
          .property-icon-wrapper {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            width: 100%;
          }
        }
        @media (min-width: 769px) {
          .property-icon-wrapper {
            display: flex;
            align-items: center;
          }
        }
      `}</style>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: '#2d3748' }}>Properties Directory</h4>
          <p className="text-muted mb-0 small">Manage and view all listed properties.</p>
        </div>
        <Button variant="primary" className="shadow-sm rounded-pill px-4" onClick={handleModalOpen} style={{ background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)', border: 'none' }}>
          <FontAwesomeIcon icon={faSearch} className="me-2" /> Find Options
        </Button>
      </div>

      <Modal show={showModal} onHide={handleModalClose} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Find Property</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-property-id">
                  <Form.Label className="small fw-semibold text-muted">PROPERTY ID</Form.Label>
                  <Form.Control
                    type="text"
                    className="rounded-3"
                    value={searchOptions.propertyId ? searchOptions.propertyId : ''}
                    onChange={(e) => onChangeFieldValue({ propertyId: parseInt(e.target.value) })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-property-title">
                  <Form.Label className="small fw-semibold text-muted">PROPERTY TITLE</Form.Label>
                  <Form.Control
                    type="text"
                    className="rounded-3"
                    value={searchOptions.propertyTitle ? searchOptions.propertyTitle : ''}
                    onChange={(e) => onChangeFieldValue({ propertyTitle: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-property-type">
                  <Form.Label className="small fw-semibold text-muted">PROPERTY TYPE</Form.Label>
                  <Select
                    options={formatEnumsForDropDown(propertyTypes)}
                    isSearchable
                    placeholder=""
                    value={
                      formatEnumsForDropDown(propertyTypes)
                        .find(option => option.value === findOptions.propertyTypeEnumId)
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        onChangeFieldValue({ propertyTypeEnumId: selectedOption?.value })
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-status">
                  <Form.Label className="small fw-semibold text-muted">STATUS</Form.Label>
                  <Select
                    options={formatStatuesForDropDown(propertyStatuses)}
                    isSearchable
                    placeholder=""
                    value={
                      formatStatuesForDropDown(propertyStatuses)
                        .find(option => option.value === findOptions.statusId)
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        onChangeFieldValue({ statusId: selectedOption.value })
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="find-owner">
              <Form.Label className="small fw-semibold text-muted">OWNER</Form.Label>
              <SearchableDropdown
                placeholder="Search owner..."
                fetchData={fetchOwners}
                onChange={(selectedValue: any) => {
                  onChangeFieldValue({ ownerId: selectedValue.value });
                  setSelectedOwner(selectedValue);
                }}
                selectedData={selectedOwner}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="is-verified-property">
              <Form.Label className="small fw-semibold text-muted me-3">IS VERIFIED?</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Yes"
                  value="yes"
                  checked={searchOptions.isVerified !== undefined ? searchOptions.isVerified === true : false}
                  onChange={handleRadioChange}
                  name="isVerified"
                  id="isVerifiedYes"
                />
                <Form.Check
                  inline
                  type="radio"
                  label="No"
                  value="no"
                  checked={searchOptions.isVerified !== undefined ? searchOptions.isVerified === false : false}
                  onChange={handleRadioChange}
                  name="isVerified"
                  id="isVerifiedNo"
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" className="rounded-pill px-4" onClick={() => {
            resetFindOptions();
            setSelectedOwner(null);
          }}>Clear</Button>
          <Button variant="primary" className="rounded-pill px-4 shadow-sm" onClick={() => onClickFind(searchOptions)}>Apply Search</Button>
        </Modal.Footer>
      </Modal>

      <Card className="modern-card mb-4">
        <div className="table-responsive">
          <Table className="modern-table" hover>
            <thead>
              <tr>
                <th style={{ width: '90px' }}>Prop #</th>
                <th>Title & Type</th>
                <th>Verified</th>
                <th>Owner Details</th>
                <th>Status & Date</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {propertiesList.map((property: ListProperty) => (
                <tr key={property.propertyId}>
                  <td data-label="Property #" className="fw-bold text-primary" style={{ fontSize: '1.05rem' }}>
                    <Link to={`${property.propertyId}`} className="text-decoration-none">#{property.propertyId}</Link>
                  </td>
                  <td data-label="Title & Type">
                    <div className="property-icon-wrapper">
                      <div className="property-icon">
                        <FontAwesomeIcon icon={faBuilding} />
                      </div>
                      <div className="text-md-start">
                        <div className="fw-bold" style={{ color: '#2d3748', fontSize: '1.05rem' }}>
                          <Link to={`${property.propertyId}`} className="text-decoration-none text-dark">{property.propertyTitle}</Link>
                        </div>
                        <Badge bg="info" className="rounded-pill mt-2 px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}>{property.propertyTypeEnumDescription}</Badge>
                      </div>
                    </div>
                  </td>
                  <td data-label="Verified">
                    <div className="text-md-start mt-2">
                      {property.isVerified ? 
                        <Badge bg="success" className="rounded-pill px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Verified</Badge> : 
                        <Badge bg="danger" className="rounded-pill px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}><FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Unverified</Badge>
                      }
                    </div>
                  </td>
                  <td data-label="Owner Details">
                    <div className="d-flex flex-column text-md-start mt-2">
                      <div className="text-dark fw-bold mb-1" style={{ fontSize: '1rem' }}>
                        <FontAwesomeIcon icon={faUser} className="me-2 text-primary" style={{ width: '18px' }} />
                        {property.ownerFullName}
                      </div>
                      <div className="text-dark fw-bold" style={{ fontSize: '1rem' }}>
                        <FontAwesomeIcon icon={faPhone} className="me-2 text-success" style={{ width: '18px' }} />
                        {property.ownerMobile || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td data-label="Status & Date">
                    <div className="d-flex flex-column text-md-start mt-2">
                      <div className="text-dark fw-bold mb-1 text-capitalize" style={{ fontSize: '1rem' }}>
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" style={{ width: '18px' }} />
                        {property.statusDescription}
                      </div>
                      <div className="text-dark fw-bold" style={{ fontSize: '1rem' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-secondary" style={{ width: '18px' }} />
                        {property.createdAt.substring(0, 10)}
                      </div>
                    </div>
                  </td>
                  <td data-label="Actions" className="text-center">
                    <div className="d-flex justify-content-end justify-content-md-center gap-2 mt-2">
                      <Button
                        variant="outline-danger"
                        className="icon-btn border-0 shadow-sm"
                        title="Delete Property"
                        onClick={() => onDeleteProperty(property.propertyId)}
                      >
                        <FontAwesomeIcon icon={faTrash as any} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {propertiesList.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted">
                    No properties found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
      
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center bg-white p-3 rounded-3 shadow-sm mb-4">
        <ViewItemsPerPage
          selectedPageSize={propertiesListPageSize}
          onSelectPageSize={onChangePageSize}
        />
        <div className="mt-3 mt-md-0">
          <Pagination
            activePage={propertiesListPageIndex + 1}
            totalItemsCount={propertiesListCount}
            itemsCountPerPage={propertiesListPageSize}
            onChange={onChangePageIndex}
            itemClass="page-item"
            linkClass='page-link shadow-none'
            firstPageText="First"
            lastPageText="Last"
          />
        </div>
      </div>
    </div>
  );
};

export default ListProperties;
